import { MessagePriority } from '../types/messages';
import { sendToTerminal } from './terminalLogger';

const SOUND_CONFIG_KEY = 'cdr:messages:sound:enabled';
const SOUND_PRIORITY_KEY = 'cdr:messages:sound:priority';
const LOG_PREFIX = '[SOUND_NOTIFICATION]';

/**
 * Configuração de notificações sonoras
 */
interface SoundConfig {
  enabled: boolean;
  priority: {
    urgente: boolean;
    alta: boolean;
    normal: boolean;
    baixa: boolean;
  };
}

/**
 * Obtém configuração de sons do localStorage
 */
function getSoundConfig(): SoundConfig {
  try {
    const enabled = localStorage.getItem(SOUND_CONFIG_KEY);
    const priority = localStorage.getItem(SOUND_PRIORITY_KEY);
    
    return {
      enabled: enabled === 'true',
      priority: priority ? JSON.parse(priority) : {
        urgente: true,
        alta: true,
        normal: false,
        baixa: false
      }
    };
  } catch {
    // Fallback: habilitado apenas para urgente e alta
    return {
      enabled: true,
      priority: {
        urgente: true,
        alta: true,
        normal: false,
        baixa: false
      }
    };
  }
}

/**
 * Salva configuração de sons no localStorage
 */
export function saveSoundConfig(config: SoundConfig): void {
  try {
    localStorage.setItem(SOUND_CONFIG_KEY, config.enabled.toString());
    localStorage.setItem(SOUND_PRIORITY_KEY, JSON.stringify(config.priority));
    
    sendToTerminal(`💾 ${LOG_PREFIX} Configuração salva`, { 
      enabled: config.enabled,
      priorities: config.priority 
    });
  } catch (error) {
    console.warn('Erro ao salvar configuração de sons:', error);
    sendToTerminal(`❌ ${LOG_PREFIX} Erro ao salvar configuração`, { 
      error: error instanceof Error ? error.message : 'Erro desconhecido' 
    });
  }
}

/**
 * Gera um tom de áudio usando Web Audio API
 */
function generateTone(frequency: number, duration: number = 200): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration / 1000);
  } catch (error) {
    // Fallback silencioso se Web Audio API não estiver disponível
    console.warn('Web Audio API não disponível:', error);
  }
}

/**
 * Toca som de notificação baseado na prioridade
 */
export function playNotificationSound(priority: MessagePriority): void {
  const config = getSoundConfig();
  
  // Se sons estão desabilitados, não fazer nada
  if (!config.enabled) {
    sendToTerminal(`🔇 ${LOG_PREFIX} Sons desabilitados`, { priority });
    return;
  }
  
  // Verificar se o som está habilitado para esta prioridade
  if (!config.priority[priority]) {
    sendToTerminal(`🔇 ${LOG_PREFIX} Som desabilitado para prioridade`, { priority });
    return;
  }
  
  // Frequências diferentes para cada prioridade
  const frequencies: Record<MessagePriority, number> = {
    urgente: 800,  // Tom mais agudo
    alta: 600,
    normal: 400,
    baixa: 300
  };
  
  const frequency = frequencies[priority] || 400;
  
  sendToTerminal(`🔔 ${LOG_PREFIX} Tocando som`, { 
    priority, 
    frequency,
    duration: priority === 'urgente' ? 300 : 200
  });
  
  // Tocar som
  generateTone(frequency, priority === 'urgente' ? 300 : 200);
}

/**
 * Obtém configuração atual de sons
 */
export function getSoundConfigValue(): SoundConfig {
  return getSoundConfig();
}

